/*
 * FieldSuggestionsCard — coaching-mode candidate picker.
 *
 * Surfaces 2-3 ready-to-commit candidate values the agent proposed
 * via the `propose_field_suggestions` tool. The user taps one chip
 * → the chosen value gets sent as their answer to the active field
 * (via sendText), and Orbit's normal extract_field pipeline writes
 * it. A "Write my own" affordance lets the user compose freely
 * through the regular composer.
 *
 * Brand: open canvas, no card surface. Each candidate is its own
 * soft-tinted row (rgba(24,25,29,0.05)) so the user sees them as
 * distinct picks, not a list. Plex Mono caps row index, Plex Sans
 * medium for the candidate value, optional Plex Serif italic for
 * rationale. Yellow text-highlighter accent only on the "you said"
 * recall — never here (state-change accent).
 */

import React from 'react';
import { ArrowRight, PenLine } from 'lucide-react';

import type { FieldSuggestionsPayload } from '../../lib/dossier-types';


export interface FieldSuggestionsCardProps {
  payload: FieldSuggestionsPayload;
  /** Pick callback — parent fires sendText(value) to commit the
   *  chosen candidate as the user's answer for the active field. */
  onPick: (value: string) => void;
  /** Optional "write my own" handler — usually a no-op since the
   *  composer is already visible below. When omitted the chip is
   *  shown but disabled to keep the keyboard affordance visible. */
  onWriteOwn?: () => void;
  disabled?: boolean;
}


export const FieldSuggestionsCard: React.FC<FieldSuggestionsCardProps> = ({
  payload,
  onPick,
  onWriteOwn,
  disabled,
}) => {
  const { intro, label } = payload;
  // Defensive: never let a malformed payload (missing/empty candidates) throw
  // during render — that blanks the whole pane. Render nothing instead.
  const candidates = Array.isArray(payload.candidates) ? payload.candidates : [];
  if (candidates.length === 0) return null;

  return (
    <div className="aa-suggestions">
      <header className="aa-suggestions-header">
        <span className="aa-suggestions-eyebrow">
          {label} · suggestions
        </span>
        {intro && (
          <p className="aa-suggestions-intro">{intro}</p>
        )}
      </header>

      <div className="aa-suggestions-grid" role="list">
        {candidates.map((c, i) => {
          const title = c.title || deriveTitle(c.value, i);
          return (
            <button
              key={`${i}-${(c.title || c.value).slice(0, 24)}`}
              type="button"
              className="aa-suggestions-block"
              onClick={() => onPick(c.value)}
              disabled={disabled}
              aria-label={`Use option ${i + 1}: ${title}`}
            >
              <span className="aa-suggestions-block-title">{title}</span>
              <span className="aa-suggestions-block-value">{c.value}</span>
              {c.rationale && (
                <span className="aa-suggestions-block-why">{c.rationale}</span>
              )}
              <span className="aa-suggestions-block-use">
                Use this <ArrowRight size={12} strokeWidth={2.5} />
              </span>
            </button>
          );
        })}
      </div>

      <footer className="aa-suggestions-footer">
        <button
          type="button"
          className="aa-suggestions-write-own"
          onClick={onWriteOwn}
          disabled={disabled || !onWriteOwn}
          aria-label="Write my own answer instead"
        >
          <PenLine size={12} strokeWidth={2.25} />
          <span>Write my own below</span>
        </button>
      </footer>
    </div>
  );
};


/**
 * Derive a short title from the first clause of the value when the
 * agent didn't supply one. Falls back to "Option N" when the value
 * starts with verbose framing.
 */
function deriveTitle(value: string, idx: number): string {
  const trimmed = value.trim();
  if (!trimmed) return `Option ${idx + 1}`;
  // Look for the first ≤ 32-char clause ending at a comma, em-dash, or
  // sentence break. Strip leading articles for tighter labels.
  const head = trimmed.split(/[—,.;:]/, 1)[0] || trimmed;
  const cleaned = head.replace(/^(the|a|an)\s+/i, '').trim();
  if (cleaned.length === 0) return `Option ${idx + 1}`;
  if (cleaned.length <= 32) return cleaned;
  return `${cleaned.slice(0, 29).trim()}…`;
}

export default FieldSuggestionsCard;
